import React from 'react';
import {
  Table as TableIcon,
  Trash2,
  Copy,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { Project, Table } from '../../../types/domain';
import { formatBytes } from './types';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';

interface AdminOrgProjectsTabProps {
  projects: Project[];
  projectTables: Record<string, Table[]>;
  expandedProjects: Record<string, boolean>;
  loadingTables: Record<string, boolean>;
  copiedToken: string | null;
  projectToDelete: Project | null;
  projectDeleteInput: string;
  isDeletingProject: boolean;
  tableToDelete: { projectId: string; table: Table } | null;
  tableDeleteInput: string;
  isDeletingTable: boolean;
  onToggleProjectExpansion: (projectId: string) => void;
  onCopyToken: (token: string) => void;
  onSelectProjectToDelete: (proj: Project | null) => void;
  setProjectDeleteInput: (val: string) => void;
  onConfirmDeleteProject: (e: React.FormEvent) => void;
  onSelectTableToDelete: (data: { projectId: string; table: Table } | null) => void;
  setTableDeleteInput: (val: string) => void;
  onConfirmDeleteTable: (e: React.FormEvent) => void;
}

export const AdminOrgProjectsTab: React.FC<AdminOrgProjectsTabProps> = ({
  projects,
  projectTables,
  expandedProjects,
  loadingTables,
  copiedToken,
  projectToDelete,
  projectDeleteInput,
  isDeletingProject,
  tableToDelete,
  tableDeleteInput,
  isDeletingTable,
  onToggleProjectExpansion,
  onCopyToken,
  onSelectProjectToDelete,
  setProjectDeleteInput,
  onConfirmDeleteProject,
  onSelectTableToDelete,
  setTableDeleteInput,
  onConfirmDeleteTable,
}) => {
  return (
    <div className="space-y-4 font-sans text-xs">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-cf-textPrimary">Organization Projects</h2>
          <p className="text-xs text-cf-textSecondary">
            Superadmin view into all project datastores. Expand a project to inspect and delete its datastore tables.
          </p>
        </div>
      </div>

      {/* In-Page Project Deletion Confirmation Banner */}
      {projectToDelete && (
        <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <AlertTriangle size={18} className="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-rose-700 dark:text-rose-300 text-sm">
                  Confirm Permanent Deletion of Project "{projectToDelete.name}"
                </h3>
                <p className="text-xs text-rose-600/90 dark:text-rose-400/90 mt-0.5">
                  This action will immediately wipe all {projectToDelete.table_count || 0} datastores, all rows, and all uploaded files stored in S3 or local object storage.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={onConfirmDeleteProject} className="flex items-center gap-3 flex-wrap pt-1">
            <div className="flex items-center gap-2">
              <label className="text-cf-textSecondary text-xs uppercase font-semibold">
                Type <strong className="text-cf-textPrimary font-sans">{projectToDelete.slug}</strong> to confirm:
              </label>
              <div className="w-48">
                <Input
                  type="text"
                  size="xs"
                  mono
                  value={projectDeleteInput}
                  onChange={(e) => setProjectDeleteInput(e.target.value)}
                  placeholder={projectToDelete.slug}
                  autoFocus
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="danger"
                size="xs"
                isLoading={isDeletingProject}
                disabled={projectDeleteInput.trim() !== projectToDelete.slug || isDeletingProject}
              >
                Delete Project & Wipe Storage
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="xs"
                onClick={() => {
                  onSelectProjectToDelete(null);
                  setProjectDeleteInput('');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {projects.length === 0 ? (
        <div className="bg-cf-surface border border-cf-border rounded-lg p-8 text-center text-cf-textMuted text-xs">
          No projects found in this organization.
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((proj) => {
            const isExpanded = !!expandedProjects[proj.id];
            const tables = projectTables[proj.id] || [];
            const isTablesLoading = !!loadingTables[proj.id];

            return (
              <div
                key={proj.id}
                className="bg-cf-surface border border-cf-border rounded-lg overflow-hidden transition-all shadow-sm"
              >
                {/* Project Header Row */}
                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => onToggleProjectExpansion(proj.id)}
                      aria-label={isExpanded ? 'Hide Tables' : 'View Tables'}
                      className="mt-0.5"
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-cf-textPrimary">{proj.name}</h3>
                        <span className="text-xs font-sans text-cf-textMuted bg-cf-bg px-2 py-0.5 rounded border border-cf-border">
                          {proj.slug}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-cf-textSecondary mt-1 flex-wrap">
                        <span>API Token Prefix: <code className="font-sans text-xs text-cf-textPrimary">{proj.api_token_prefix}...</code></span>
                        <span>•</span>
                        <span>Datastores: <strong className="text-cf-textPrimary">{proj.table_count || 0}</strong></span>
                        <span>•</span>
                        <span>Submissions: <strong className="text-cf-textPrimary">{proj.submission_count || 0}</strong></span>
                        <span>•</span>
                        <span>Storage: <strong className="text-cf-textPrimary">{formatBytes(proj.storage_bytes)}</strong></span>
                        <span>•</span>
                        <span>Created: {new Date(proj.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Project Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      type="button"
                      variant="secondary"
                      size="xs"
                      onClick={() => onToggleProjectExpansion(proj.id)}
                      rightIcon={
                        <span className="text-xs bg-cf-surface px-1.5 py-0.2 rounded border border-cf-border">
                          {proj.table_count || 0}
                        </span>
                      }
                    >
                      {isExpanded ? 'Hide Tables' : 'View Tables'}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="icon-xs"
                      onClick={() => {
                        onSelectProjectToDelete(proj);
                        setProjectDeleteInput('');
                      }}
                      title="Delete Project & Files"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                {/* Expanded Datastores Section */}
                {isExpanded && (
                  <div className="border-t border-cf-border bg-cf-bg/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs uppercase tracking-wider text-cf-textSecondary font-semibold flex items-center gap-1.5">
                        <TableIcon size={13} className="text-blue-500" />
                        <span>Datastores in this Project</span>
                      </h4>
                    </div>

                    {/* In-Page Datastore Deletion Confirmation Banner */}
                    {tableToDelete && tableToDelete.projectId === proj.id && (
                      <div className="mb-3 p-3.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs space-y-2.5">
                        <div className="flex items-start gap-2">
                          <AlertTriangle size={16} className="text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-rose-700 dark:text-rose-300 text-xs">
                              Delete Datastore "{tableToDelete.table.name}" ({tableToDelete.table.slug})
                            </h4>
                            <p className="text-xs text-rose-600/90 dark:text-rose-400/90">
                              All rows and uploaded files will be permanently purged from database and S3 storage.
                            </p>
                          </div>
                        </div>

                        <form onSubmit={onConfirmDeleteTable} className="flex items-center gap-2.5 flex-wrap">
                          <label className="text-cf-textSecondary text-xs uppercase font-semibold">
                            Type <strong className="text-cf-textPrimary font-sans">{tableToDelete.table.slug}</strong>:
                          </label>
                          <div className="w-40">
                            <Input
                              type="text"
                              size="xs"
                              mono
                              value={tableDeleteInput}
                              onChange={(e) => setTableDeleteInput(e.target.value)}
                              placeholder={tableToDelete.table.slug}
                              autoFocus
                            />
                          </div>
                          <Button
                            type="submit"
                            variant="danger"
                            size="xs"
                            isLoading={isDeletingTable}
                            disabled={tableDeleteInput.trim() !== tableToDelete.table.slug || isDeletingTable}
                          >
                            Confirm Delete
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="xs"
                            onClick={() => {
                              onSelectTableToDelete(null);
                              setTableDeleteInput('');
                            }}
                          >
                            Cancel
                          </Button>
                        </form>
                      </div>
                    )}

                    {isTablesLoading ? (
                      <div className="py-4 flex items-center justify-center text-xs text-cf-textMuted gap-2">
                        <Loader2 size={14} className="animate-spin text-cf-orange" />
                        <span>Loading datastores...</span>
                      </div>
                    ) : tables.length === 0 ? (
                      <div className="text-xs text-cf-textMuted py-3 text-center border border-dashed border-cf-border rounded">
                        No datastores found in this project.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-cf-border rounded-lg bg-cf-surface">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-cf-tableHeader dark:bg-[#2e323c] border-b border-cf-border dark:border-[#2e323c] text-cf-textSecondary uppercase text-xs">
                            <tr>
                              <th className="py-2.5 px-3">Table Name</th>
                              <th className="py-2.5 px-3">Slug</th>
                              <th className="py-2.5 px-3">Public Ingest Token</th>
                              <th className="py-2.5 px-3">Mode</th>
                              <th className="py-2.5 px-3">Storage</th>
                              <th className="py-2.5 px-3">Created</th>
                              <th className="py-2.5 px-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-cf-border">
                            {tables.map((table) => (
                              <tr key={table.id} className="bg-cf-surface hover:bg-cf-surfaceHover/50 transition-colors">
                                <td className="py-2.5 px-3 font-semibold text-cf-textPrimary">
                                  {table.name}
                                </td>
                                <td className="py-2.5 px-3 font-sans text-cf-textSecondary">
                                  {table.slug}
                                </td>
                                <td className="py-2.5 px-3 font-sans">
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-cf-textPrimary truncate max-w-[140px]">
                                      {table.ingest_token}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon-xs"
                                      onClick={() => onCopyToken(table.ingest_token || '')}
                                      title="Copy Ingest Token"
                                    >
                                      {copiedToken === table.ingest_token ? (
                                        <Check size={11} className="text-emerald-500" />
                                      ) : (
                                        <Copy size={11} />
                                      )}
                                    </Button>
                                  </div>
                                </td>
                                <td className="py-2.5 px-3">
                                  <span className="px-1.5 py-0.5 rounded text-xs uppercase tracking-wider bg-cf-bg border border-cf-border text-cf-textSecondary font-sans">
                                    {table.schema_mode || 'DYNAMIC'}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-cf-textSecondary">
                                  {formatBytes(table.storage_bytes)} ({table.files_count || 0} files)
                                </td>
                                <td className="py-2.5 px-3 text-cf-textMuted">
                                  {new Date(table.created_at).toLocaleDateString()}
                                </td>
                                <td className="py-2.5 px-3 text-right">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-xs"
                                    onClick={() => {
                                      onSelectTableToDelete({ projectId: proj.id, table });
                                      setTableDeleteInput('');
                                    }}
                                    title="Delete Datastore & Files"
                                    className="hover:text-rose-400"
                                  >
                                    <Trash2 size={13} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
